import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Loader2 } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { storeAuth } from "@/lib/auth";
import { readApiError } from "@/lib/format";

// Reject any `next` value that doesn't start with a single `/` (prevents
// open-redirect attacks like next=//evil.com or next=https://evil.com).
function safeNext(raw) {
  if (!raw) return "/poc";
  if (typeof raw !== "string") return "/poc";
  if (!raw.startsWith("/")) return "/poc";
  if (raw.startsWith("//")) return "/poc";
  return raw;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/auth/login`, {
        email: email.trim(),
        password,
      });
      storeAuth(res.data.access_token, res.data.user);
      toast.success(`Welcome back, ${res.data.user?.name || "Kenny"}`);
      navigate(nextPath, { replace: true });
    } catch (e) {
      toast.error("Login failed", { description: readApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <section
        data-testid="login-page"
        className="mx-auto flex max-w-md flex-col px-6 py-20"
      >
        <div className="mb-8 flex items-center gap-3">
          <span className="rounded-full border border-primary/40 bg-primary/10 p-2 text-primary">
            <Leaf className="h-5 w-5" />
          </span>
          <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
            Owner login
          </p>
        </div>
        <h1 className="font-serif text-4xl">Welcome back, Kenny.</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This is the back-of-house entrance — for the founder only. Members
          and customers don&apos;t need to log in.
        </p>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5 rounded-2xl border border-border/60 bg-card p-6"
        >
          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kenny@coastalrootsbotanica.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Logging in…
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Not Kenny?{" "}
          <Link to="/" className="text-primary hover:underline">
            Back to the front of the shop.
          </Link>
        </p>
      </section>
    </PublicLayout>
  );
}
